/**
 * ErrorBoundary Component
 *
 * Global error boundary that catches React errors and displays a fallback UI.
 * Prevents the entire application from crashing due to uncaught errors.
 *
 * Features:
 * - Catches errors in React component tree
 * - Displays user-friendly error message
 * - Provides recovery actions (retry, go home)
 * - Logs errors for debugging
 * - Optional custom fallback UI
 * - Optional error callback for external logging services
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/base/Button';
import { AlertTriangle, Home, RefreshCcw } from 'lucide-react';
import { logger } from '@/lib/utils/logger';

interface Props {
  /**
   * Child components to render
   */
  children: ReactNode;
  /**
   * Custom fallback UI to show when error occurs
   */
  fallback?: ReactNode;
  /**
   * Callback when error is caught (for external logging services)
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * Show error details in development mode
   */
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary class component
 * Note: Error boundaries must be class components as of React 18
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  /**
   * Update state when error is caught
   */
  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error details
   */
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error
    logger.error('ErrorBoundary caught an error:', error, errorInfo);

    // Store error info in state for display
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // In production, you might want to send to error tracking service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  /**
   * Reset error boundary state
   */
  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Navigate to home
   */
  private handleGoHome = () => {
    this.handleReset();
    window.location.href = '/';
  };

  /**
   * Reload page
   */
  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = import.meta.env.DEV;
      const showDetails = this.props.showDetails ?? isDev;

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-2xl text-center">
            {/* Error Icon */}
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-10 w-10 text-destructive" />
            </div>

            {/* Error Message */}
            <h1 className="mb-3 text-3xl font-bold">Something went wrong</h1>
            <p className="mb-6 text-lg text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>

            {/* Error Details (Development Only) */}
            {showDetails && this.state.error && (
              <div className="mb-6 rounded-lg border bg-muted/50 p-4 text-left">
                <h2 className="mb-2 text-sm font-semibold">Error Details (Development)</h2>
                <div className="space-y-2 text-xs">
                  <div>
                    <strong>Error:</strong>
                    <pre className="mt-1 overflow-x-auto rounded bg-background p-2">
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="mt-1 overflow-x-auto rounded bg-background p-2">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                  {this.state.error.stack && (
                    <div>
                      <strong>Stack Trace:</strong>
                      <pre className="mt-1 overflow-x-auto rounded bg-background p-2">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={this.handleReset} leftIcon={<RefreshCcw className="h-4 w-4" />}>
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={this.handleGoHome}
                leftIcon={<Home className="h-4 w-4" />}
              >
                Go Home
              </Button>
              <Button variant="outline" onClick={this.handleReload}>
                Reload Page
              </Button>
            </div>

            {/* Help Text */}
            <p className="mt-6 text-sm text-muted-foreground">
              If the problem persists, please contact support or try refreshing the page.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook to programmatically throw errors to error boundary
 * Useful for testing error boundaries or handling async errors
 */
export function useErrorBoundary() {
  const [, setState] = React.useState();

  return React.useCallback((error: Error) => {
    setState(() => {
      throw error;
    });
  }, []);
}
